// School admin: every student in the school's active classes, with whether the school has
// the consent it requires from them. Consent is recorded on each class's roster; this page
// is where an admin sees who is still missing it.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { ConsentStatusPage, fetchConsentStatus } from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
  tableStyle,
  tdStyle,
  thStyle,
} from './classesStyles';

const logger = createLogger('ConsentStatusTab');

interface Props {
  organizationId?: string | null;
}

const ConsentStatusTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<ConsentStatusPage | null>(null);
  const [filter, setFilter] = useState<'missing' | 'granted' | ''>('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setPage(await fetchConsentStatus(filter, organizationId));
    } catch (err) {
      logger.error('Failed to load consent status', err);
      setError(errorMessage(err, t('studentAdmin.consent.couldNotLoad')));
    }
  }, [organizationId, filter, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section style={cardStyle} aria-labelledby="consent-status-heading">
      <h2 id="consent-status-heading" style={sectionHeadingStyle}>
        {t('studentAdmin.consent.title')}
      </h2>
      {error && <div style={errorStyle} role="alert">{error}</div>}
      {!page ? (
        !error && <LoadingIndicator message={t('studentAdmin.consent.loading')} />
      ) : (
        <>
          <p style={{ ...hintStyle, marginTop: 0 }}>
            {page.consent_required
              ? t('studentAdmin.consent.required', { type: t(`classes.consentTypes.${page.consent_type}`) })
              : t('studentAdmin.consent.notRequired')}
          </p>
          <p style={{ fontSize: '0.9rem', margin: '0.75rem 0' }} role="status">
            {t('studentAdmin.consent.counts', {
              total: page.total,
              withConsent: page.with_consent,
              missing: page.missing_consent,
            })}
          </p>
          <select
            aria-label={t('studentAdmin.consent.filter')}
            style={{ ...inputStyle, marginBottom: '1rem' }}
            value={filter}
            onChange={e => setFilter(e.target.value as 'missing' | 'granted' | '')}
          >
            <option value="">{t('common.all')}</option>
            <option value="missing">{t('studentAdmin.consent.onlyMissing')}</option>
            <option value="granted">{t('studentAdmin.consent.onlyGranted')}</option>
          </select>
          {page.items.length === 0 ? (
            <p style={hintStyle}>{t('studentAdmin.consent.none')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('common.name')}</th>
                    <th style={thStyle}>{t('common.email')}</th>
                    <th style={thStyle}>{t('studentAdmin.consent.classes')}</th>
                    <th style={thStyle}>{t('classes.consent')}</th>
                    <th style={thStyle}>{t('studentAdmin.consent.grantedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map(entry => (
                    <tr key={entry.student_id}>
                      <td style={tdStyle}>{entry.display_name || entry.email || entry.student_id}</td>
                      <td style={tdStyle}>{entry.email || '—'}</td>
                      <td style={tdStyle}>{entry.classes.join(', ')}</td>
                      <td style={tdStyle}>
                        <span style={statusBadgeStyle(entry.has_consent)}>
                          {entry.has_consent ? t('classes.consentGiven') : t('classes.consentMissing')}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {entry.granted_at ? new Date(entry.granted_at).toLocaleDateString(uiLocale()) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={hintStyle}>{t('studentAdmin.consent.recordOnRoster')}</p>
        </>
      )}
    </section>
  );
};

export default ConsentStatusTab;
